import React from 'react';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import axios from 'axios';
import * as Routes from '@/modules/routes';
import SPASectionHeading from '../shared/SPASectionHeading';
import BulkUnassignDialog from './BulkUnassignDialog';
import RepairSummary from './RepairSummary';
import type { BulkPayload } from './BulkUnassignDialog';
import type { RepairFilter, RowRepair } from './repairs';
import type { AssignmentRow } from './useAssignmentRows';
import type { MatchPayload } from './useCourseMatch';

// AIDEV-NOTE: This is the header over the assignments table, not a table of its own. It used to render
// its own rows, so a teacher's page showed the same assignments twice with different columns — the
// wiring now lives on the table's rows, and only the summary and the action live here.
interface CourseMatchPanelProps {
  assignments?: AssignmentRow[];
  checking: boolean;
  filter: RepairFilter;
  onFilter: (filter: RepairFilter) => void;
  /** Null until the rosters, the gradebooks and the rows are all in. */
  repairs: Map<number, RowRepair> | null;
  match: { data?: MatchPayload; isFetching: boolean; error: unknown };
  stafferId: string;
}

const CourseMatchPanel = ({
  assignments, checking, filter, match, onFilter, repairs, stafferId,
}: CourseMatchPanelProps) => {
  const queryClient = useQueryClient();

  // The plan is asked for unconfirmed first and rendered before anything is written — a bulk assignee
  // change is the largest thing this dashboard can do, and it should never be one click away.
  const bulk = useMutation({
    mutationFn: async (confirmed: boolean): Promise<BulkPayload> => {
      const { data } = await axios.post(
        Routes.plato_api_sysadmin_grade_sync_bulk_assignees_path(),
        { confirmed, staffer_id: stafferId },
      );

      return data.data;
    },
    onSuccess: (result) => {
      if (result.confirmed !== true) return;

      queryClient.invalidateQueries({ queryKey: ['gradeSyncCourseMatch', stafferId] });
      queryClient.invalidateQueries({ queryKey: ['gradeSyncRepairs'] });
      queryClient.invalidateQueries({ queryKey: ['gradeSyncAssignments'] });
    },
  });

  if (stafferId.trim() === '') return null;

  const map = match.data?.class_map;

  return (
    <div className="tw-mb-6">
      <SPASectionHeading title="What to repair" />
      {(match.isFetching || checking) && (
        <p className="tw-m-0 tw-text-sm tw-text-spa-secondary">Reading LMS rosters and gradebooks…</p>
      )}
      {match.error != null && (
        <p className="tw-m-0 tw-text-sm tw-text-spa-error">Could not read the rosters.</p>
      )}
      {match.data?.error != null && (
        <p className="tw-m-0 tw-text-sm tw-text-spa-error">{match.data.error}</p>
      )}

      {map != null && assignments != null && repairs != null && (
        <RepairSummary
          assignments={assignments}
          bulkBusy={bulk.isLoading}
          classes={map.classes}
          filter={filter}
          repairs={repairs}
          onBulk={() => bulk.mutate(false)}
          onFilter={onFilter}
        />
      )}

      {/* AIDEV-NOTE: A dialog rather than more rows in the panel. The plan is a different shape from
          the table around it — per assignment, every class on it, and which are staged — and rendering
          it inline read as though the button had already acted. */}
      <BulkUnassignDialog
        busy={bulk.isLoading}
        open={bulk.data != null}
        plan={bulk.data}
        onCancel={() => bulk.reset()}
        onConfirm={() => bulk.mutate(true)}
      />
    </div>
  );
};

export default CourseMatchPanel;
